/**
 * Download a file across desktop and mobile.
 *
 * Desktop browsers honour the anchor `download` attribute, so we fetch the
 * file as a blob and click a synthetic anchor — the file lands in Downloads
 * with the right name.
 *
 * iOS/iPadOS Safari (and any iOS browser, since they all use WebKit) does NOT
 * honour `download` for blob URLs and buffers the whole PDF in memory, which
 * is what makes large guides freeze the tab. There we hand the URL straight to
 * the native viewer, which streams the file and offers Save / Share.
 */
function isWebKitMobile(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ reports as Macintosh but has touch points.
  const iPadOS = /Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
  return iOS || iPadOS;
}

export async function downloadFile(url: string, filename?: string): Promise<void> {
  const name = filename || url.split("/").pop()?.split("?")[0] || "download";

  // Must run inside the click gesture, before any await, or Safari blocks it.
  if (isWebKitMobile()) {
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (!win) window.location.href = url;
    return;
  }

  try {
    const res = await fetch(url, { credentials: "omit" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = name;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  } catch {
    // Fallback: open in a new tab; user can save from there.
    window.open(url, "_blank", "noopener,noreferrer");
  }
}
